import {
  ActionFunctionArgs,
  LoaderFunction,
  LoaderFunctionArgs,
  Await,
  useActionData,
  useLoaderData,
  useNavigation,
  useParams,
  Outlet,
} from 'react-router';
import { addPlanMileage } from '@/catalog/utils/actions/add-plan-mileage';
import { appFetch } from '@/util-react-router/app-fetch';
import { appData } from '@/util-react-router/app-data';
import { appRedirect } from '@/util-react-router/app-redirect';
import { buildPageMeta } from '@/util-react-router/build-page-meta';
import { CatalogMarketAction } from '@/catalog/types/catalog-market-action';
import { CatalogMarketLoader, VehiclesData } from '@/catalog/types/catalog-market-loader';
import { colors } from '@/theme/colors';
import { findNearestZipcode } from '@/catalog/utils/actions/find-nearest-zipcode';
import { Suspense, useEffect, useState } from 'react';
import { fullstoryCheckoutPropsFromSession } from '@/fullstory/utils/fullstory-checkout-props-from-session';
import { fullstoryEvent } from '@/fullstory/utils/fullstory-event';
import { getSwapEligibility } from '@/common/utils/swap-state';
import { kebabCaseToTitleCase } from '@/util-conversion/misc/kebab-case-to-title-case';
import { startingAtPrice } from '@/common/constants/seo-constants';
import { submitAddSavingsCalculatorParams } from '@/catalog/utils/actions/submit-add-savings-calculator-params';
import { submitCreditAge } from '@/catalog/utils/actions/submit-credit-age';
import { UserFlow, getRedirectPath } from '@/common/utils/get-redirect-path';
import { useSendGTMEvent } from '@/common/hooks/google-tag-manager/use-send-gtm-event';
import * as gtag from '@/common/utils/gtags.client';
import CatalogContentContainer from '@/catalog/components/catalog-content-container';
import getCookieSession from '@/util-react-router/get-cookie-session';
import logResponseError from '@/util-react-router/log-response-error';
import PageErrorBanners from '@/common/components/page-error-banners';
import PromoCodeBanner from '@/catalog/components/promo-code-banner';
import Spacer from '@/common/components/spacer';
import StatusBannerDismissible from '@/common/components/status-banner-dismissible';
import type { LinksFunction, MetaFunction, ShouldRevalidateFunction } from 'react-router';
import vehicleTilesGridStyles from '@/catalog/styles/vehicle-tiles-grid.css?url';
import hasCompletedIntroSelections from '@/catalog/utils/has-completed-intro-selections';
import ZipCreditAgeMultiStepDrawerDialog from '@/catalog/components/intro-modal/zipcode-credit-age-multi-step-drawer-dialog';
import { updatePlanMileageCreditAndAge } from '@/catalog/utils/actions/update-plan-mileage-credit-and-age';
import { useDigiohModalPageWrapper } from '@/common/hooks/digioh/use-digioh-modal-page-wrapper';
import { addZipcode } from '@/catalog/utils/actions/add-zipcode';
import PageLayout, { withPageContainerStyles } from '@/common/components/page-layout';
import HeaderContainer from '@/catalog/components/header/header-container';
import HowItWorks from '@/catalog/components/how-it-works';
import Faqs from '@/catalog/components/faqs';
import MobileSubHeaders from '@/catalog/components/filters/mobile-sub-header';
import appendFilterParams from '@/catalog/utils/append-filter-params';
import SidePanelFilters from '@/catalog/components/side-panel-filters/side-panel-filters';
import VehicleTiles from '@/catalog/components/vehicle-tiles';
import shouldHideCreditModal from '@/catalog/hooks/should-hide-credit-modal';
import TotalSortSubHeader from '@/catalog/components/total-sort-sub-header';
import InventoryNoResultFound from '@/catalog/components/inventory-no-result-found';
import InventoryNoStock from '@/catalog/components/inventory-no-stock';
import InventoryFilterDrawerDialog from '@/catalog/components/side-panel-filters/inventory-filter-drawer-dialog';
import MobileFilterHeader from '@/catalog/components/side-panel-filters/mobile-filter-header';
import useInventoryFilters from '@/catalog/hooks/use-inventory-filters';
import parseRawFilters from '@/catalog/utils/parse-raw-filters';
import parseRawFilterCounts from '@/catalog/utils/parse-raw-filter-counts';
import { useBreakpoint } from '@/common/hooks/use-breakpoint';
import VehicleTileSkeletonGrid from '@/catalog/components/vehicle-tile-skeleton-grid';
import { cn } from '@/shadcn-ui/utils';
import LowerShippingFeesBanner from '@/catalog/lower-shipping-fees-banner';
import getPromoCode from '@/common/utils/get-promo-code';
import MercedesInventoryBanner from '@/catalog/components/mercedes-inventory-banner';
import { useScrollToVehicleTile } from '@/catalog/hooks/use-scroll-to-vehicle-tile';

export const meta: MetaFunction = ({ params }) => {
  return buildPageMeta({
    title: params.market
      ? `Flexible car leases in ${kebabCaseToTitleCase(params.market)} | SUVs, sedans, & more.`
      : 'Flexible car leases | SUVs, sedans, & more.',
    description: params.market
      ? `Zero down. Cancel anytime. Cars starting at ${startingAtPrice}. Ditch the dealership and save thousands with Flexcar: Greater ${kebabCaseToTitleCase(params.market)}'s only month-to-month car lease.`
      : `Zero down. Cancel anytime. Cars starting at ${startingAtPrice}. Ditch the dealership and save thousands with Flexcar.`,
  });
};

export const links: LinksFunction = () => {
  return [{ rel: 'stylesheet', href: vehicleTilesGridStyles }];
};

export const shouldRevalidate: ShouldRevalidateFunction = ({
  formData,
  defaultShouldRevalidate,
  currentUrl,
  nextUrl,
}) => {
  if (currentUrl.pathname.includes('waitlist') || nextUrl.pathname.includes('waitlist')) {
    return false;
  }

  const intent = formData ? formData.get('intent') : undefined;

  // Skip revalidation for savings calculator fetch as it's fully
  // decoupled from the inventory data
  if (intent === 'fetch_savings_calculator' || intent === 'find_nearest_zipcode') {
    return false;
  }

  return defaultShouldRevalidate;
};

export async function action({ request, params }: ActionFunctionArgs) {
  const formData = await request.formData();

  const intent = formData.get('intent');

  switch (intent) {
    case 'add_zipcode': {
      return await addZipcode({ formData, request, params });
    }
    case 'find_nearest_zipcode': {
      return await findNearestZipcode({ formData, request });
    }
    case 'add_credit_and_age': {
      // Add to cart
      const { response } = await submitCreditAge({ formData, request });

      if (!response.ok) {
        logResponseError('catalog - add_credit_and_age - action', response);
        const responseData: CatalogMarketAction = {
          intent,
          ok: false,
          errors: {
            add_credit_and_age_error: 'Failed to add credit and age to cart',
          },
        };
        return appData({ data: responseData, response });
      }

      const responseData: CatalogMarketAction = {
        intent,
        closeIntroModal: true,
        ok: true,
      };
      return appData({ data: responseData, response });
    }
    case 'add_plan_mileage': {
      return await addPlanMileage({ formData, request, isPDP: false });
    }
    case 'fetch_savings_calculator': {
      try {
        const { errors, response } = await submitAddSavingsCalculatorParams({
          formData,
          request,
        });

        if (errors) {
          return { errors };
        }

        if (!response.ok) {
          logResponseError(
            'inventory - calculate savings - calculator v2 - action',
            response
          );

          return {
            errors: {
              error: 'Failed to calculate savings.',
            },
          };
        }

        const data = await response.json();

        return appData({
          data: { vehicle: data.vehicle },
          response,
        });
      } catch {
        return {
          errors: {
            error: 'Failed to calculate savings.',
          },
        };
      }
    }
    case 'update_plan_mileage_credit_and_age': {
      return await updatePlanMileageCreditAndAge({ formData, request });
    }
    default: {
      const responseData: CatalogMarketAction = {
        intent: 'invalid_intent',
        ok: false,
        errors: { invalid_intent_error: 'Invalid intent' },
      };

      return responseData;
    }
  }
}

export const loader: LoaderFunction = async ({ request }: LoaderFunctionArgs) => {
  const requestUrl = new URL(request.url);

  // Get market id from last url slug
  const marketId = requestUrl.pathname.split('/')?.[2] ?? undefined;

  if (marketId === 'boston') {
    return appRedirect({
      url: `/inventory/new-england`,
      response: new Response('Boston expanded to the New England regional area.'),
    });
  }

  const marketsResponse = await appFetch({
    url: '/api/external/v1/markets/',
    request,
  });

  if (!marketsResponse.ok) {
    logResponseError('catalog - markets - loader', marketsResponse);
    return { errors: { error: 'Failed to fetch markets' } };
  }

  const { markets, plan_fullstory_event } = await marketsResponse.json();
  if (!markets.some((market: { id: string }) => market.id === marketId)) {
    throw new Response(`Market Not Found for ${marketId}`, { status: 404 });
  }

  // Construct a URL to hit Rails
  const catalogUrl = new URL(`${process.env.PUBLIC_BACKEND_URL}/api/external/v1/catalog`);

  const filterOptionsUrl = new URL(
    `${process.env.PUBLIC_BACKEND_URL}/api/external/v1/catalog/filters`
  );

  appendFilterParams(requestUrl, marketId, [catalogUrl, ...[filterOptionsUrl]]);

  const creditAgeUrl = new URL(
    `${process.env.PUBLIC_BACKEND_URL}/api/external/v1/risk/credit_scores_and_ages`
  );
  const mileagePlansUrl = new URL(
    `${process.env.PUBLIC_BACKEND_URL}/api/external/v1/mileage_plans`
  );

  const [
    creditAgeResponse,
    mileagePlansResponse,
    filterOptionsResponse,
    accountDetailsResponse,
  ] = await Promise.all([
    appFetch({ url: creditAgeUrl, request }),
    appFetch({ url: mileagePlansUrl, request }),
    appFetch({ url: filterOptionsUrl, request }),
    appFetch({
      url: '/api/external/v1/accounts/details',
      request,
    }),
  ]);

  const [
    { credit_scores, age_ranges },
    { available_plans },
    { filters, vehicle_waitlist_enabled, session },
    { account, already_qualified },
  ] = await Promise.all([
    creditAgeResponse.json(),
    mileagePlansResponse.json(),
    filterOptionsResponse.json(),
    accountDetailsResponse.json(),
  ]);

  if (!creditAgeResponse.ok) {
    logResponseError('catalog - credit-age - loader', creditAgeResponse);
    //todo: improve error handling such that more UI can be shown in these cases https://goflexcar.atlassian.net/browse/RUBY-330
    return appData({
      data: {
        errors: { error: 'Failed to retrieve credit age, please try again later.' },
      },
      response: creditAgeResponse,
    });
  }

  if (!mileagePlansResponse.ok) {
    logResponseError('catalog - mileage-plans - loader', mileagePlansResponse);
    //todo: improve error handling such that more UI can be shown in these cases https://goflexcar.atlassian.net/browse/RUBY-330
    return appData({
      data: {
        errors: { error: 'Failed to retrieve mileage plans, please try again later.' },
      },
      response: mileagePlansResponse,
    });
  }

  if (!filterOptionsResponse.ok) {
    // Log failed response but fail gracefully as it shouldn't block inventory load
    logResponseError('catalog - filter - loader', filterOptionsResponse);
  }

  const redirectPath = getRedirectPath({
    path: requestUrl.pathname,
    flow: session?.can_swap ? UserFlow.swap : UserFlow.checkout,
    session,
  });

  // Note: If this redirect happens, it suggests the backend was unable
  // to add this market_id to the user's cart. Given this would block
  // subsequent actions, we may consider removing this redirect.
  if (redirectPath) {
    return appRedirect({ url: redirectPath, response: filterOptionsResponse });
  }

  const cookieSession = await getCookieSession(request);
  const isPersonalizedPriceVehicleSearch = cookieSession.get(
    'isPersonalizedPriceVehicleSearch'
  );

  const catalogData = appFetch({ url: catalogUrl, request }).then(async (response) => {
    if (!response.ok) {
      logResponseError('catalog - catalog - loader', response);

      return {
        errors: { error: 'Failed to retrieve inventory, please try again later.' },
      };
    }
    return response.json();
  });

  //  Defer allows us to immediately return data as well as defer data loads via promise. In this case vehicle data is deferred to be loaded later
  return appData({
    data: {
      session: session,
      creditScores: credit_scores,
      ageRanges: age_ranges,
      mileagePlans: available_plans,
      filterOptions: parseRawFilters(filters),
      filterCounts: parseRawFilterCounts(
        filters?.counts,
        process.env.SHOULD_USE_FILTER_COUNTS === 'true'
      ),
      markets,
      planFullstoryEvent: plan_fullstory_event,
      subscription: account?.subscription,
      isPersonalizedPriceVehicleSearch,
      PUBLIC_CDN_URL: process.env.PUBLIC_CDN_URL,
      MEMBERSHIP_COST: process.env.MEMBERSHIP_COST,
      PROMO_CODE_MILE_REWARD: process.env.PROMO_CODE_MILE_REWARD,
      INVENTORY_TUTORIAL_ENABLED: process.env.INVENTORY_TUTORIAL_ENABLED === 'true',
      MILEAGE_PLAN_OPTIONS: process.env.MILEAGE_PLAN_OPTIONS?.split(',') ?? [],
      VEHICLE_WAITLIST_ENABLED: vehicle_waitlist_enabled,
      FADE_IN_VEHICLE_TILE_IMAGES: process.env.FADE_IN_VEHICLE_TILE_IMAGES === 'true',
      LOWER_SHIPPING_FEES_BANNER_ENABLED:
        process.env.LOWER_SHIPPING_FEES_BANNER_ENABLED === 'true',
      vehicleDeferred: catalogData,
      alreadyQualified: already_qualified,
      // Below are deprecated values that we still need to pass down for clients with cached code
      SHOULD_ANIMATE_IMAGES: false,
      MERCEDES_BANNER: process.env.MERCEDES_BANNER === 'true',
    },
    response: marketsResponse,
  });
};

export default function MarketCatalog() {
  const params = useParams();

  const market = params.market;
  const {
    errors,
    session,
    subscription,
    creditScores,
    ageRanges,
    mileagePlans,
    filterOptions,
    filterCounts,
    markets,
    planFullstoryEvent,
    isPersonalizedPriceVehicleSearch,
    vehicleDeferred,
    PUBLIC_CDN_URL,
    MEMBERSHIP_COST,
    INVENTORY_TUTORIAL_ENABLED,
    MILEAGE_PLAN_OPTIONS,
    VEHICLE_WAITLIST_ENABLED,
    LOWER_SHIPPING_FEES_BANNER_ENABLED,
    FADE_IN_VEHICLE_TILE_IMAGES,
    alreadyQualified,
    MERCEDES_BANNER,
  } = useLoaderData<CatalogMarketLoader>();
  const actionData = useActionData<CatalogMarketAction>();
  const { state, location } = useNavigation();

  const [planOpen, setPlanOpen] = useState(false);
  const planHandleOpen = () => setPlanOpen(true);
  const planHandleClose = () => setPlanOpen(false);

  const isNavigatingToWaitlist = location?.pathname?.includes('/waitlist');
  const showLoadingSkeleton = state !== 'idle' && !isNavigatingToWaitlist;

  const { canViewSwap, canLegacySwap } = getSwapEligibility(subscription);

  const selectedCreditScoreId = session?.cart?.credit_score_id;
  const introSelections = hasCompletedIntroSelections({
    selectedCreditScoreId,
    selectedZipcode: session?.cart?.zipcode,
    selectedIsOver25: session?.cart?.is_over_25,
    selectedAgeRangeId: session?.cart?.age_range_id,
    isUsingAgeRange: ageRanges !== undefined && ageRanges?.length > 0,
    ageRanges: ageRanges ?? [],
  });

  const hideCreditModal = shouldHideCreditModal(subscription);
  const [creditOpen, setCreditOpen] = useState(false);
  const creditHandleClose = () => setCreditOpen(false);
  const creditHandleOpen = () => setCreditOpen(true);

  const {
    selectedFilters,
    setSelectedFilters,
    handleClearAll,
    hasNoFilters,
    filterCount,
  } = useInventoryFilters({ filterOptions });

  const [introModalOpen, setIntroModalOpen] = useState(
    !introSelections.hasSelectedZipcodeCreditAge ? true : false
  );
  const introModalHandleClose = () => setIntroModalOpen(false);
  const introModalHandleOpen = () => setIntroModalOpen(true);
  const isDesktop = useBreakpoint() === 'desktop';
  const [inventoryFilterModalOpen, setInventoryFilterModalOpen] = useState(false);
  const inventoryFilterModalHandleClose = () => setInventoryFilterModalOpen(false);
  const inventoryFilterModalHandleOpen = () => setInventoryFilterModalOpen(true);
  const [hasChangedSelectedPlan, setHasChangedSelectedPlan] = useState(false);

  const gtmFormattedMarketName = market === 'new-england' ? 'New England' : market;

  useEffect(() => {
    if (gtmFormattedMarketName && market) {
      gtag.pushData({ name: gtmFormattedMarketName, market_link: market });
    }
  }, [gtmFormattedMarketName, market]);
  useSendGTMEvent({
    event_name: 'cars_in_location',
    condition: (gtmFormattedMarketName && market) !== undefined,
  });

  useDigiohModalPageWrapper({ pageSource: 'inventory' });

  useEffect(() => {
    if (actionData?.ok) {
      if (actionData.intent === 'add_credit_and_age' && actionData.closeIntroModal) {
        creditHandleClose();
      }
      if (actionData.closeIntroModal) {
        introModalHandleClose();
      }
      if (actionData.intent === 'add_plan_mileage') {
        planHandleClose();
        setHasChangedSelectedPlan(true);
      }
    }
  }, [actionData, session]);

  useEffect(() => {
    if (introSelections.hasSelectedZipcodeCreditAge && introModalOpen) {
      setIntroModalOpen(false);
    }
  }, [introModalOpen, introSelections, introSelections.hasSelectedZipcodeCreditAge]);

  // Scroll to top whenever we modify filters
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [selectedFilters]);

  const fullstoryEventDelegate = (mileage: string) => {
    fullstoryEvent('monthly_plan_changed_inventory', { mileage });
    if (session) {
      fullstoryEvent('plan_changed', {
        ...fullstoryCheckoutPropsFromSession(session),
        vehicle_mileage: mileage,
      });
    }
  };

  const headerContent = (
    <HeaderContainer
      session={session}
      planAgeCreditConfig={{
        // Plan-related configuration
        planHandleClose,
        planOpen: isDesktop && planOpen,
        planHandleOpen,
        planError: actionData?.errors?.add_plan_mileage_error,

        // Credit and age configuration
        creditHandleClose,
        creditOpen: isDesktop && creditOpen,
        creditHandleOpen,
        creditError: actionData?.errors?.add_credit_and_age_error,
        creditScores,
        ageRanges,

        // Selection states
        selectedPlanMileage: session?.cart?.plan_mileage,
        selectedCreditScoreId,
        selectedAgeRangeId: session?.cart?.age_range_id,
        selectedAge: session?.cart?.is_over_25 ?? true,

        // Feature flags
        canViewSwap,
        canLegacySwap,

        // Additional configuration
        mileagePlans,
        hideCreditModal,
        fullstoryEventDelegate,
        alreadyQualified,
      }}
    />
  );
  const currentMarket = markets?.find((market) => market.id === params.market);
  const { promoCode, promoCodeReward } = getPromoCode({ session });

  const scrollToTileConfig = useScrollToVehicleTile();

  return (
    <>
      {LOWER_SHIPPING_FEES_BANNER_ENABLED ? (
        <div className="sticky top-0 z-10 w-full lg:static">
          <LowerShippingFeesBanner />
        </div>
      ) : null}

      <PageLayout
        backgroundColor="bg-none"
        disablePageContainer={true}
        disableFooterMargin={true}
        hideCatalogLink={true}
        headerContentSpacerVariant="no-spacer"
        headerLeftContent={headerContent}
        navLinkClassName="text-flexcar-neutral1"
        hamburgerFillPrimary={colors.neutral1}
      >
        <div className="relative flex w-full flex-col items-center">
          {errors ? (
            <CatalogContentContainer>
              <PageErrorBanners loaderErrors={errors} />
            </CatalogContentContainer>
          ) : (
            <>
              <div className={withPageContainerStyles('block max-w-[1440px] lg:hidden')}>
                <MobileSubHeaders
                  planHandleClose={planHandleClose}
                  planOpen={!isDesktop && planOpen}
                  planHandleOpen={planHandleOpen}
                  planError={actionData?.errors?.add_plan_mileage_error}
                  creditHandleClose={creditHandleClose}
                  creditOpen={!isDesktop && creditOpen}
                  creditHandleOpen={creditHandleOpen}
                  creditError={actionData?.errors?.add_credit_and_age_error}
                  creditScores={creditScores}
                  ageRanges={ageRanges}
                  selectedPlanMileage={session?.cart?.plan_mileage}
                  selectedCreditScoreId={selectedCreditScoreId}
                  selectedAgeRangeId={session?.cart?.age_range_id}
                  selectedAge={session?.cart?.is_over_25 ?? true}
                  canViewSwap={canViewSwap}
                  canLegacySwap={canLegacySwap}
                  mileagePlans={mileagePlans}
                  hideCreditModal={hideCreditModal}
                  fullstoryEventDelegate={fullstoryEventDelegate}
                  alreadyQualified={alreadyQualified}
                />
              </div>

              {!isPersonalizedPriceVehicleSearch && !session?.cart?.promo_code ? (
                <Spacer variant="height24" hideForBreakpoints={['sm', 'md']} />
              ) : null}
              <Spacer variant="height16" hideForBreakpoints={['lg', 'xl', '2xl']} />

              {/* Promo code banner on desktop. This needs to be outside linear gradient div so it doesn't affect the gradient line. */}
              {promoCode && promoCodeReward ? (
                <div className="hidden w-full lg:block">
                  <PromoCodeBanner promoCode={promoCode} reward={promoCodeReward} />
                  <Spacer variant="height24" />
                </div>
              ) : null}

              <MobileFilterHeader
                onClickDelegate={inventoryFilterModalHandleOpen}
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
                filterCount={filterCount}
                className={cn({
                  'top-[36px] max-[377px]:top-[52px]': LOWER_SHIPPING_FEES_BANNER_ENABLED,
                })}
              />
              <Spacer variant="height8" hideForBreakpoints={['lg', 'xl', '2xl']} />

              {/* Personalized vehicle search banner. This needs to be outside linear gradient div so it doesn't affect the gradient line. */}
              {isPersonalizedPriceVehicleSearch ? (
                <div className={withPageContainerStyles('max-w-[1440px]')}>
                  <StatusBannerDismissible
                    variant="info"
                    message={{
                      text: 'Here is your personalized list of eligible vehicles!',
                    }}
                    closable={{ storageKey: 'personalized_price_vehicle_search' }}
                    className="mb-[24px] mt-[8px] lg:mt-0"
                  />
                </div>
              ) : null}

              <div className="relative flex w-full flex-col items-center justify-center lg:bg-[linear-gradient(white_144px,_#F4F5F6_144px_100%)]">
                {/* Promo code banner on mobile */}
                {session?.cart?.promo_code && session?.cart?.promo_code_reward ? (
                  <div className="block w-full lg:hidden">
                    <Spacer variant="height16" />
                    <PromoCodeBanner
                      promoCode={session?.cart?.promo_code}
                      reward={session?.cart?.promo_code_reward}
                    />
                    <Spacer variant="height24" />
                  </div>
                ) : null}

                <div className={withPageContainerStyles('max-w-[1440px]')}>
                  <Suspense fallback={<TotalSortSubHeader />}>
                    <Await<VehiclesData> resolve={vehicleDeferred as VehiclesData}>
                      {({ total }) => <TotalSortSubHeader total={total} />}
                    </Await>
                  </Suspense>

                  <Spacer variant="height24" hideForBreakpoints={['sm', 'md']} />
                  <Spacer variant="height16" hideForBreakpoints={['lg', 'xl', '2xl']} />

                  <div className="flex flex-col gap-[40px] lg:flex-row">
                    <SidePanelFilters
                      filterOptions={filterOptions}
                      selectedFilters={selectedFilters}
                      setSelectedFilters={setSelectedFilters}
                      handleClearAll={handleClearAll}
                      hasNoFilters={hasNoFilters}
                      filterCount={filterCount}
                      filterCounts={filterCounts}
                      selectedPlanMileage={session?.cart?.plan_mileage}
                      canViewSwap={canViewSwap}
                      vehicleWaitlistEnabled={VEHICLE_WAITLIST_ENABLED}
                    />

                    <Suspense fallback={<VehicleTileSkeletonGrid />}>
                      <Await<VehiclesData> resolve={vehicleDeferred as VehiclesData}>
                        {({
                          vehicles,
                          total,
                          inventory_fullstory_event,
                          is_savings_calculator_v2_available,
                          errors,
                        }) => {
                          if (errors) {
                            return <PageErrorBanners loaderErrors={errors} />;
                          }

                          if (!showLoadingSkeleton && !total) {
                            return hasNoFilters ? (
                              <InventoryNoStock
                                total={total}
                                className="h-fit rounded-[16px] border-[1px] border-dashed border-flexcar-neutral60 bg-flexcar-neutral100/20 px-[24px] py-[80px] backdrop-blur-[12px]"
                              />
                            ) : (
                              <InventoryNoResultFound />
                            );
                          } else if (showLoadingSkeleton && !total) {
                            return <VehicleTileSkeletonGrid />;
                          }

                          return (
                            <div className="flex flex-col gap-8">
                              {MERCEDES_BANNER ? (
                                <MercedesInventoryBanner
                                  PUBLIC_CDN_URL={PUBLIC_CDN_URL}
                                />
                              ) : null}
                              <div className="vehicle-tiles-grid vehicle-tiles-grid-side-filter h-fit">
                                <VehicleTiles
                                  market={market}
                                  vehicles={vehicles}
                                  introModalOpen={introModalOpen}
                                  inventory_fullstory_event={inventory_fullstory_event}
                                  plan_fullstory_event={planFullstoryEvent}
                                  is_savings_calculator_v2_available={
                                    is_savings_calculator_v2_available
                                  }
                                  session={session}
                                  showLoadingSkeleton={showLoadingSkeleton}
                                  creditScores={creditScores}
                                  ageRanges={ageRanges ?? []}
                                  subscription={subscription}
                                  PUBLIC_CDN_URL={PUBLIC_CDN_URL}
                                  INVENTORY_TUTORIAL_ENABLED={INVENTORY_TUTORIAL_ENABLED}
                                  MEMBERSHIP_COST={MEMBERSHIP_COST}
                                  MILEAGE_PLAN_OPTIONS={MILEAGE_PLAN_OPTIONS}
                                  vehicleWaitlistEnabled={VEHICLE_WAITLIST_ENABLED}
                                  total={total}
                                  hasChangedSelectedPlan={hasChangedSelectedPlan}
                                  setHasChangedSelectedPlan={setHasChangedSelectedPlan}
                                  fadeInImage={FADE_IN_VEHICLE_TILE_IMAGES}
                                  scrollToTileConfig={scrollToTileConfig}
                                />
                              </div>
                            </div>
                          );
                        }}
                      </Await>
                    </Suspense>
                  </div>
                </div>

                {VEHICLE_WAITLIST_ENABLED ? <Outlet /> : null}
              </div>

              {introModalOpen ? (
                <ZipCreditAgeMultiStepDrawerDialog
                  open={introModalOpen}
                  handleOpen={introModalHandleOpen}
                  handleClose={introModalHandleClose}
                  creditScores={creditScores}
                  selectedZipcode={session?.cart?.zipcode}
                  ageRanges={ageRanges}
                  selectedAgeRangeId={session?.cart?.age_range_id}
                  alreadyQualified={alreadyQualified}
                />
              ) : null}

              <Suspense
                fallback={
                  <InventoryFilterDrawerDialog
                    filterOptions={filterOptions}
                    selectedFilters={selectedFilters}
                    setSelectedFilters={setSelectedFilters}
                    handleClearAll={handleClearAll}
                    hasNoFilters={hasNoFilters}
                    filterCount={filterCount}
                    filterCounts={filterCounts}
                    handleClose={inventoryFilterModalHandleClose}
                    open={inventoryFilterModalOpen}
                    selectedPlanMileage={session?.cart?.plan_mileage}
                    canViewSwap={canViewSwap}
                  />
                }
              >
                <Await<VehiclesData> resolve={vehicleDeferred as VehiclesData}>
                  {({ total }) => (
                    <InventoryFilterDrawerDialog
                      filterOptions={filterOptions}
                      selectedFilters={selectedFilters}
                      setSelectedFilters={setSelectedFilters}
                      handleClearAll={handleClearAll}
                      hasNoFilters={hasNoFilters}
                      filterCount={filterCount}
                      filterCounts={filterCounts}
                      handleClose={inventoryFilterModalHandleClose}
                      open={inventoryFilterModalOpen}
                      vehicleTotal={total}
                      selectedPlanMileage={session?.cart?.plan_mileage}
                      canViewSwap={canViewSwap}
                      vehicleWaitlistEnabled={VEHICLE_WAITLIST_ENABLED}
                    />
                  )}
                </Await>
              </Suspense>

              <HowItWorks />

              <Faqs
                variant="dark"
                markets={markets && markets.length ? markets : []}
                currentMarket={currentMarket}
              />
            </>
          )}
        </div>
      </PageLayout>
    </>
  );
}
